import { z } from 'zod';

export function sanitizeWizard<
  Step extends string,
  Data extends Partial<Record<Step, unknown>>,
>(args: {
  data: Data;
  done: Record<Step, boolean>;
  steps: readonly Step[];
  defaults: Partial<Record<Step, Data[Step]>>;
  schema: z.ZodObject<Record<Step, z.ZodType>>;
}): {
  cleanedData: Data;
  cleanedDone: Record<Step, boolean>;
  dataChanged: boolean;
  doneChanged: boolean;
} {
  // Shallow copies: only top-level step fields are reassigned
  const cleanedData: Data = { ...args.data };
  const cleanedDone: Record<Step, boolean> = { ...args.done };

  let locked = false;
  let dataChanged = false;
  let doneChanged = false;

  for (const step of args.steps) {
    // If already locked, reset subsequent steps to defaults and mark done=false
    if (locked) {
      const nextVal = args.defaults[step]; // type: Data[Step] | undefined
      if (cleanedData[step] !== nextVal) {
        cleanedData[step] = nextVal as Data[typeof step];
        dataChanged = true;
      }
      if (cleanedDone[step]) {
        cleanedDone[step] = false;
        doneChanged = true;
      }
      continue;
    }

    // If current step is not done -> lock from here
    if (!cleanedDone[step]) {
      locked = true;
      if (cleanedDone[step]) {
        cleanedDone[step] = false;
        doneChanged = true;
      }
      continue;
    }

    // Validate current step using its field schema
    const stepSchema = args.schema.shape[step]; // ZodType<unknown>
    const isValid = stepSchema.safeParse(cleanedData[step]).success;

    if (!isValid) {
      // Mark current as not done; keep its value; lock following
      if (cleanedDone[step]) {
        cleanedDone[step] = false;
        doneChanged = true;
      }
      locked = true;
    }
  }

  return { cleanedData, cleanedDone, dataChanged, doneChanged };
}
